import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  Accessibility, 
  X, 
  Eye, 
  Type, 
  Zap,
  Sun
} from "lucide-react";
import { useAccessibility } from "./AccessibilityProvider";
import { cn } from "@/lib/utils";

export function AccessibilityPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    highContrast,
    toggleHighContrast,
    reducedMotion,
    toggleReducedMotion,
    fontSize,
    setFontSize
  } = useAccessibility();

  return (
    <>
      {/* Toggle button */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-40 rounded-full h-12 w-12 shadow-lg bg-card border-border hover:bg-accent"
        aria-label="Abrir configurações de acessibilidade"
        aria-haspopup="dialog"
      >
        <Accessibility className="h-5 w-5" />
      </Button>

      {/* Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-50 bg-black/50"
              aria-hidden="true"
            />

            {/* Panel content */}
            <motion.div
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="accessibility-title"
              className="fixed left-0 top-0 bottom-0 z-50 w-80 max-w-[90vw] bg-card border-r border-border shadow-xl overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Accessibility className="h-5 w-5 text-primary" />
                  </div>
                  <h2 id="accessibility-title" className="font-semibold text-lg">
                    Acessibilidade
                  </h2>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  aria-label="Fechar painel de acessibilidade"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Settings */}
              <div className="p-4 space-y-6">
                {/* High Contrast */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Eye className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-medium">Visão</h3>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <Label htmlFor="high-contrast" className="cursor-pointer">
                      <div>Alto contraste</div>
                      <div className="text-xs text-muted-foreground">
                        Aumenta o contraste das cores
                      </div>
                    </Label>
                    <Switch
                      id="high-contrast"
                      checked={highContrast}
                      onCheckedChange={toggleHighContrast}
                      aria-describedby="high-contrast-desc"
                    />
                  </div>
                </div>

                {/* Font Size */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Type className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-medium">Tamanho do texto</h3>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: "normal", label: "Normal", size: "text-sm" },
                      { value: "large", label: "Grande", size: "text-base" },
                      { value: "extra-large", label: "Extra", size: "text-lg" }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setFontSize(option.value as "normal" | "large" | "extra-large")}
                        className={cn(
                          "p-3 rounded-lg border transition-all text-center",
                          fontSize === option.value
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border bg-muted/30 hover:bg-muted"
                        )}
                        aria-pressed={fontSize === option.value}
                      >
                        <span className={option.size}>{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Reduced Motion */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Zap className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-medium">Movimento</h3>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <Label htmlFor="reduced-motion" className="cursor-pointer">
                      <div>Reduzir animações</div>
                      <div className="text-xs text-muted-foreground">
                        Remove animações e transições
                      </div>
                    </Label>
                    <Switch
                      id="reduced-motion"
                      checked={reducedMotion}
                      onCheckedChange={toggleReducedMotion}
                    />
                  </div>
                </div>

                {/* Keyboard shortcuts */}
                <div className="space-y-3">
                  <h3 className="font-medium flex items-center gap-2">
                    <Sun className="h-5 w-5 text-muted-foreground" />
                    Atalhos de teclado
                  </h3>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between p-2 bg-muted/30 rounded">
                      <span>Navegar</span>
                      <kbd className="px-2 py-0.5 bg-muted rounded text-xs">Tab</kbd>
                    </div>
                    <div className="flex justify-between p-2 bg-muted/30 rounded">
                      <span>Voltar</span>
                      <kbd className="px-2 py-0.5 bg-muted rounded text-xs">Shift + Tab</kbd>
                    </div>
                    <div className="flex justify-between p-2 bg-muted/30 rounded">
                      <span>Ativar</span>
                      <kbd className="px-2 py-0.5 bg-muted rounded text-xs">Enter</kbd>
                    </div>
                    <div className="flex justify-between p-2 bg-muted/30 rounded">
                      <span>Fechar</span>
                      <kbd className="px-2 py-0.5 bg-muted rounded text-xs">Esc</kbd>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-border bg-muted/30">
                <p className="text-xs text-muted-foreground text-center">
                  Conformidade WCAG AAA • Navegação por teclado completa
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
